import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Icon, Popup, Label, Dropdown } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { AreaForm } from "../../Areas/AreaForm";
import { TopicForm } from "../../Topics/TopicForm/TopicForm";
import { Training, Employee, Area, Topic } from "../../../../api";
import { OHS_STATUSES } from "../../../../utils/constants";
import { useAuth } from "../../../../hooks";
import { useSite } from "../../../../contexts/SiteContext";
import { useLanguage } from "../../../../contexts";
import searchSvg from "../../../../assets/svg/search.svg";
import "./CapacitacionesTable.scss";

const AVATAR_COLORS = ["#1A5FA0", "#0E376E", "#3D6B9E", "#648EBC", "#4F7FB5", "#2A4F80", "#8AB3D4"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const capController      = new Training();
const employeeController = new Employee();
const areaController     = new Area();
const topicController    = new Topic();

const MODALITY_OPTIONS = [
  { key: "in_person",            value: "in_person",            text: "Presencial" },
  { key: "virtual_synchronous",  value: "virtual_synchronous",  text: "Virtual / Online sincrónica" },
  { key: "virtual_asynchronous", value: "virtual_asynchronous", text: "Virtual / Online asincrónica (e-learning)" },
  { key: "blended",              value: "blended",              text: "Blended (combinada)" },
  { key: "on_the_job",           value: "on_the_job",           text: "Práctica en planta / On the job" },
  { key: "simulation",           value: "simulation",           text: "Simulacro" },
];

const INSTRUCTOR_OPTIONS = [
  { key: "internal_ehs",          value: "internal_ehs",          text: "Responsable EHS interno" },
  { key: "occupational_physician",value: "occupational_physician", text: "Médico laboral" },
  { key: "external_trainer",      value: "external_trainer",      text: "Capacitador externo" },
  { key: "vendor_manufacturer",   value: "vendor_manufacturer",   text: "Proveedor / Fabricante" },
  { key: "government_agency",     value: "government_agency",     text: "Organismo oficial (SRT, STPS, etc.)" },
  { key: "other",                 value: "other",                 text: "Otro" },
];

const FREQUENCY_OPTIONS = [
  { key: "on_boarding", value: "on_boarding", text: "Por ingreso (inducción)" },
  { key: "monthly",     value: "monthly",     text: "Mensual" },
  { key: "quarterly",   value: "quarterly",   text: "Trimestral" },
  { key: "semiannual",  value: "semiannual",  text: "Semestral" },
  { key: "annual",      value: "annual",      text: "Anual" },
  { key: "task_change", value: "task_change", text: "Por cambio de tarea / puesto" },
  { key: "by_event",    value: "by_event",    text: "Por evento / accidente" },
  { key: "on_demand",   value: "on_demand",   text: "A demanda" },
];

const STATUS_OPTIONS = OHS_STATUSES.map(s => ({ key: s.key, value: s.value }));

const RESULT_OPTIONS = [
  { key: "passed",        value: "passed",        text: "Aprobado" },
  { key: "failed",        value: "failed",        text: "Desaprobado — requiere repetición" },
  { key: "no_evaluation", value: "no_evaluation", text: "Sin evaluación (solo asistencia)" },
  { key: "absent",        value: "absent",        text: "Ausente" },
];

const REGISTRATION_TYPE_OPTIONS = [
  { key: "signed_registry_arg", value: "signed_registry_arg", text: "Registro firmado (ARG — Ley 19.587)" },
  { key: "dc3_mex",             value: "dc3_mex",             text: "Constancia DC-3 (MEX — STPS)" },
  { key: "digital_attendance",  value: "digital_attendance",  text: "Lista de asistencia digital" },
  { key: "external_certificate",value: "external_certificate",text: "Certificado de curso externo" },
  { key: "not_required",        value: "not_required",        text: "Ninguno requerido" },
];

const STATUS_COLOR = {
  pending: "grey", in_progress: "blue", completed: "green",
  not_applicable: "olive", overdue: "orange", cancelled: "red",
};

const EMPTY = {
  site: "", title: "", area: "", topic: "",
  date: "", durationHours: 0, location: "",
  modality: "", instructor: "", instructorName: "", frequency: "", status: "pending",
  attendees: [], registrationType: "",
};

const InlineSelect = ({ options, value, onChange, placeholder }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="cap-table__inline-select">
    <option value="">{placeholder || "—"}</option>
    {options.map((o) => <option key={o.key} value={o.value}>{o.text}</option>)}
  </select>
);

const InlineSelectWithNew = ({ options, value, onChange, placeholder, newLabel }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="cap-table__inline-select"
  >
    <option value="">{placeholder || "—"}</option>
    {options.map((o) => <option key={o.key} value={o.value}>{o.text}</option>)}
    <option value="__new__" className="cap-table__option-new">{newLabel}</option>
  </select>
);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-AR") : "-";
const getId   = (val) => (val && typeof val === "object" ? val._id : val) || "";

export function CapacitacionesTable({ reload, onReload, triggerNew, extYear }) {
  const { accessToken, user } = useAuth();
  const { site: contextSite } = useSite();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const isFirstRender = useRef(true);

  const [items,        setItems]        = useState(null);
  const [filtered,     setFiltered]     = useState([]);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [areas,     setAreas]     = useState([]);
  const [employees, setEmployees] = useState([]);
  const [topics,    setTopics]    = useState([]);

  const [editingId,     setEditingId]     = useState(null);
  const [editingData,   setEditingData]   = useState(EMPTY);
  const [saving,        setSaving]        = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [pendingFiles,  setPendingFiles]  = useState([]);
  const [empSelection,  setEmpSelection]  = useState([]);
  const [showAreaModal,  setShowAreaModal]  = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);

  const [selected,    setSelected]    = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const [showObs,       setShowObs]       = useState(false);
  const [obsItem,       setObsItem]       = useState(null);
  const [obsVal,        setObsVal]        = useState("");
  const [editingObsIdx, setEditingObsIdx] = useState(null);
  const [editingObsVal, setEditingObsVal] = useState("");

  const [showAttach,   setShowAttach]   = useState(false);
  const [showFiles,    setShowFiles]    = useState(false);
  const [fileItem,     setFileItem]     = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const fileInputRef         = useRef(null);
  const fileInputEvidenceRef = useRef(null);

  // Load global catalogues (areas, topics)
  useEffect(() => {
    (async () => {
      try {
        const [areasRes, topicsRes] = await Promise.all([
          areaController.getAreas(accessToken, true),
          topicController.getTopics(accessToken, true),
        ]);
        setAreas(areasRes.map((a) => ({ key: a._id, value: a._id, text: a.name })));
        setTopics(topicsRes.map((t) => ({ key: t._id, value: t._id, text: t.name })));
      } catch {}
    })();
  }, []);

  // Load employees filtered by site
  useEffect(() => {
    (async () => {
      try {
        const filters = { active: true };
        if (contextSite) filters.site = contextSite;
        const empsRes = await employeeController.getEmployees(accessToken, filters);
        setEmployees(
          empsRes.map((e) => ({
            key:    e._id,
            value:  e._id,
            text:   `${e.legajo ? e.legajo + " — " : ""}${e.name || `${e.firstname || ""} ${e.lastname || ""}`.trim()}`.trim(),
            legajo: e.legajo || "",
            name:   e.name   || `${e.firstname || ""} ${e.lastname || ""}`.trim(),
          }))
        );
      } catch {}
    })();
  }, [contextSite]);

  // Load trainings
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const params = { active: true };
        if (contextSite) params.site = contextSite;
        if (extYear)     params.year = extYear;
        const res = await capController.getCapacitaciones(accessToken, params);
        setItems(res);
        setFiltered(res);
      } catch {
        setItems([]);
        setFiltered([]);
      }
    })();
  }, [reload, extYear]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (triggerNew > 0) startNew();
  }, [triggerNew]);

  useEffect(() => {
    if (!items) return;
    let result = [...items];
    if (statusFilter) result = result.filter((p) => p.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((p) =>
        p.customId?.toString().includes(q) ||
        p.title?.toLowerCase().includes(q) ||
        (p.area?.name || "").toLowerCase().includes(q) ||
        (p.topic?.name || "").toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, statusFilter, items]);

  const reloadAreas = async () => {
    const prevIds = new Set(areas.map((a) => a.value));
    const res = await areaController.getAreas(accessToken, true);
    const mapped = res.map((a) => ({ key: a._id, value: a._id, text: a.name }));
    setAreas(mapped);
    const added = mapped.find((a) => !prevIds.has(a.value));
    if (added) setField("area", added.value);
    setShowAreaModal(false);
  };

  const reloadTopics = async () => {
    const prevIds = new Set(topics.map((t) => t.value));
    const res = await topicController.getTopics(accessToken, true);
    const mapped = res.map((t) => ({ key: t._id, value: t._id, text: t.name }));
    setTopics(mapped);
    const added = mapped.find((t) => !prevIds.has(t.value));
    if (added) setField("topic", added.value);
    setShowTopicModal(false);
  };

  const startNew = () => {
    setEditingId("new");
    setEditingData({ ...EMPTY, site: contextSite || "" });
    setActiveSection("details");
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditingData({
      site:             contextSite || getId(item.site),
      title:            item.title            || "",
      area:             getId(item.area),
      topic:            getId(item.topic),
      date:             item.date             ? new Date(item.date).toISOString().slice(0, 10) : "",
      durationHours:    item.durationHours    || 0,
      location:         item.location         || "",
      modality:         item.modality         || "",
      instructor:       item.instructor       || "",
      instructorName:   item.instructorName   || "",
      frequency:        item.frequency        || "",
      status:           item.status           || "pending",
      attendees:        (item.attendees || []).map((a) => ({
        employee: getId(a.employee),
        legajo:   a.legajo  || "",
        name:     a.name    || "",
        result:   a.result  || "",
      })),
      registrationType: item.registrationType || "",
    });
    setActiveSection("details");
  };

  const cancelEdit = () => { setEditingId(null); setEditingData(EMPTY); setActiveSection(null); setPendingFiles([]); setEmpSelection([]); };

  const setField = (f, v) => setEditingData((p) => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!editingData.site) { toast.error("El sitio es obligatorio."); return; }
    setSaving(true);
    try {
      const payload = {
        ...editingData,
        durationHours: Number(editingData.durationHours) || 0,
        date:          editingData.date || undefined,
        sector:        editingData.sector || undefined,
        attendees:     editingData.attendees.map((a) => ({
          employee: a.employee || undefined,
          legajo:   a.legajo   || undefined,
          name:     a.name     || undefined,
          result:   a.result   || undefined,
        })),
      };
      if (editingId === "new") {
        const created = await capController.createCapacitacion(accessToken, payload);
        if (pendingFiles.length > 0 && created?._id) {
          for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append("file", file);
            await capController.uploadFile(accessToken, created._id, fd);
          }
          setPendingFiles([]);
        }
        toast.success("Capacitación creada.");
      } else {
        await capController.updateCapacitacion(accessToken, editingId, payload);
        toast.success("Capacitación actualizada.");
      }
      cancelEdit();
      onReload();
    } catch (err) {
      toast.error(err?.msg || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (p) => { setSelected(p); setShowConfirm(true); };

  const handleConfirm = async () => {
    try {
      await capController.deleteCapacitacion(accessToken, selected._id);
      toast.success("Capacitación eliminada.");
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || "Error."); }
  };

  const openObs = (p) => { setObsItem(p); setObsVal(""); setEditingObsIdx(null); setEditingObsVal(""); setShowObs(true); };

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSaving(true);
    try {
      const userName = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email || "Usuario";
      const updatedObs = [...(obsItem.observations || []), { text: obsVal.trim(), userName, createdAt: new Date() }];
      await capController.updateCapacitacion(accessToken, obsItem._id, { observations: updatedObs });
      toast.success("Observación guardada.");
      setShowObs(false);
      onReload();
    } catch { toast.error("Error al guardar."); }
    finally { setSaving(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).map((o, i) =>
        i === editingObsIdx ? { ...o, text: editingObsVal.trim() } : o
      );
      await capController.updateCapacitacion(accessToken, obsItem._id, { observations: updatedObs });
      toast.success("Observación actualizada.");
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      setEditingObsIdx(null); setEditingObsVal("");
      onReload();
    } catch { toast.error("Error al actualizar."); }
    finally { setSaving(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).filter((_, i) => i !== origIdx);
      await capController.updateCapacitacion(accessToken, obsItem._id, { observations: updatedObs });
      toast.success("Observación eliminada.");
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      onReload();
    } catch { toast.error("Error al eliminar."); }
    finally { setSaving(false); }
  };

  const openAttach = (p) => { setFileItem(p); setShowAttach(true); };
  const openFiles  = (p) => { setFileItem(p); setShowFiles(true); };

  const handleUpload = async () => {
    const files = fileInputRef.current?.files;
    if (!files || files.length === 0) { toast.warning("Seleccioná al menos un archivo."); return; }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        await capController.uploadFile(accessToken, fileItem._id, fd);
      }
      toast.success("Archivos subidos.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowAttach(false);
      onReload();
    } catch { toast.error("Error al subir archivos."); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await capController.deleteFile(accessToken, fileItem._id, fileKey);
      toast.success("Archivo eliminado.");
      setFileItem((prev) => ({ ...prev, files: prev.files.filter((f) => f.key !== fileKey) }));
      onReload();
    } catch { toast.error("Error al eliminar."); }
    finally { setDeletingFile(null); }
  };

  const handleViewFile = async (fileKey) => {
    try {
      const url = await capController.getFile(accessToken, fileKey);
      if (!url) { toast.error("No se pudo obtener el archivo."); return; }
      window.open(url, "_blank");
    } catch { toast.error("Error al abrir el archivo."); }
  };

  const handleUploadEvidence = async () => {
    const files = fileInputEvidenceRef.current?.files;
    if (!files || files.length === 0) { toast.warning("Seleccioná al menos un archivo."); return; }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        await capController.uploadFile(accessToken, editingId, fd);
      }
      toast.success("Archivos subidos.");
      if (fileInputEvidenceRef.current) fileInputEvidenceRef.current.value = "";
      onReload();
    } catch { toast.error("Error al subir archivos."); }
    finally { setUploading(false); }
  };

  const handleDeleteFileInline = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await capController.deleteFile(accessToken, editingId, fileKey);
      toast.success("Archivo eliminado.");
      onReload();
    } catch { toast.error("Error al eliminar."); }
    finally { setDeletingFile(null); }
  };

  // Attendees helpers
  const addAttendee = () => {
    setEditingData((p) => ({ ...p, attendees: [...p.attendees, { employee: "", legajo: "", name: "", result: "" }] }));
  };

  const addSelectedEmployees = () => {
    if (!empSelection.length) return;
    const newRows = empSelection.map((id) => {
      const emp = employees.find((e) => e.value === id);
      return { employee: id, legajo: emp?.legajo || "", name: emp?.name || "", result: "" };
    });
    setEditingData((p) => ({ ...p, attendees: [...p.attendees, ...newRows] }));
    setEmpSelection([]);
  };
  const removeAttendee = (idx) => {
    setEditingData((p) => ({ ...p, attendees: p.attendees.filter((_, i) => i !== idx) }));
  };
  const setAttendeeField = (idx, field, val) => {
    setEditingData((p) => {
      const attendees = p.attendees.map((a, i) =>
        i !== idx ? a : { ...a, [field]: val }
      );
      return { ...p, attendees };
    });
  };

  // ── Edit row ───────────────────────────────────────────────────────────
  const renderEditRow = () => (
    <Table.Row key="editing" positive={editingId === "new"} warning={editingId !== "new"}>
      <Table.Cell style={{ fontSize: "0.8em", color: "#888" }}>
        {editingId === "new" ? "Nueva" : `#${items?.find((p) => p._id === editingId)?.customId || "—"}`}
      </Table.Cell>
      <Table.Cell>
        <input type="text" value={editingData.title} onChange={(e) => setField("title", e.target.value)}
          placeholder="Título" className="cap-table__inline-input" />
      </Table.Cell>
      <Table.Cell>
        <InlineSelectWithNew
          options={areas} value={editingData.area}
          onChange={(v) => v === "__new__" ? setShowAreaModal(true) : setField("area", v)}
          placeholder="Área" newLabel="+ Nueva área"
        />
      </Table.Cell>
      <Table.Cell>
        <InlineSelectWithNew
          options={topics} value={editingData.topic}
          onChange={(v) => v === "__new__" ? setShowTopicModal(true) : setField("topic", v)}
          placeholder="Tema" newLabel="+ Nuevo tema"
        />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={MODALITY_OPTIONS} value={editingData.modality} onChange={(v) => setField("modality", v)} placeholder="Modalidad" />
      </Table.Cell>
      <Table.Cell>
        <input type="date" value={editingData.date} onChange={(e) => setField("date", e.target.value)}
          className="cap-table__inline-input" />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={STATUS_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.status} onChange={(v) => setField("status", v)} placeholder={t("status")} />
      </Table.Cell>
      <Table.Cell textAlign="center">—</Table.Cell>
      <Table.Cell textAlign="center" className="cap-table__actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSave} className="cap-table__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}><Icon name="close" /></Button>
        } />
      </Table.Cell>
    </Table.Row>
  );

  // ── Section panel ──────────────────────────────────────────────────────
  const SECTIONS = [
    { key: "details",   label: "Detalles" },
    { key: "attendees", label: "Asistentes" },
  ];

  const renderSectionPanel = () => {
    if (!editingId) return null;

    const evidenceFiles = editingId !== "new"
      ? (items?.find(p => p._id === editingId)?.files || [])
      : [];

    const panelContent = {
      details: (
        <div className="cap-table__section-fields">
          <div className="cap-table__field-grid">
            <div className="cap-table__field-row">
              <label>Capacitador / Instructor</label>
              <InlineSelect options={INSTRUCTOR_OPTIONS} value={editingData.instructor} onChange={(v) => setField("instructor", v)} placeholder="Seleccionar" />
            </div>
            {editingData.instructor === "other" && (
              <div className="cap-table__field-row">
                <label>Nombre del instructor</label>
                <input type="text" className="cap-table__inline-input"
                  value={editingData.instructorName} onChange={(e) => setField("instructorName", e.target.value)}
                  placeholder="Nombre completo..." />
              </div>
            )}
            <div className="cap-table__field-row">
              <label>Frecuencia</label>
              <InlineSelect options={FREQUENCY_OPTIONS} value={editingData.frequency} onChange={(v) => setField("frequency", v)} placeholder="Seleccionar" />
            </div>
            <div className="cap-table__field-row">
              <label>Duración (horas)</label>
              <input type="number" min="0" step="0.5" className="cap-table__inline-input"
                value={editingData.durationHours} onChange={(e) => setField("durationHours", e.target.value)} />
            </div>
            <div className="cap-table__field-row">
              <label>Lugar</label>
              <input type="text" className="cap-table__inline-input"
                value={editingData.location} onChange={(e) => setField("location", e.target.value)}
                placeholder="Sala, dirección..." />
            </div>
          </div>
        </div>
      ),
      attendees: (
        <div className="cap-table__section-fields">

          {/* ── Registro y evidencia ─────────────────────── */}
          <div className="cap-table__field-grid" style={{ marginBottom: "0.75rem" }}>
            <div className="cap-table__field-row">
              <label>Tipo de registro de asistencia</label>
              <InlineSelect options={REGISTRATION_TYPE_OPTIONS} value={editingData.registrationType}
                onChange={(v) => setField("registrationType", v)} placeholder="Seleccionar" />
            </div>
          </div>

          <div className="cap-table__evidence-header">
            <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Evidencia adjunta</span>
            <span style={{ fontSize: "0.8rem", color: "#888", marginLeft: "0.5rem" }}>
              Foto del listado firmado, PDF del certificado, etc.
            </span>
          </div>

          {editingId === "new" ? (
            <>
              <div className="cap-table__evidence-upload">
                <input type="file" multiple
                  onChange={(e) => {
                    const selected = Array.from(e.target.files || []);
                    setPendingFiles((prev) => [...prev, ...selected]);
                    e.target.value = "";
                  }}
                  style={{ flex: 1, fontSize: "0.85rem" }} />
              </div>
              {pendingFiles.length === 0 ? (
                <p style={{ color: "#999", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                  Los archivos seleccionados se subirán al guardar.
                </p>
              ) : (
                <ul className="cap-table__evidence-list">
                  {pendingFiles.map((f, i) => (
                    <li key={i}>
                      <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} />
                      <span className="cap-table__evidence-name">{f.name}</span>
                      <Button circular icon size="mini" color="red"
                        onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}>
                        <Icon name="times" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className="cap-table__evidence-upload">
                <input type="file" multiple ref={fileInputEvidenceRef}
                  style={{ flex: 1, fontSize: "0.85rem" }} />
                <Button size="mini" primary loading={uploading} disabled={uploading}
                  onClick={handleUploadEvidence}>
                  <Icon name="upload" /> Subir
                </Button>
              </div>
              {evidenceFiles.length === 0 ? (
                <p style={{ color: "#999", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                  Sin archivos adjuntos.
                </p>
              ) : (
                <ul className="cap-table__evidence-list">
                  {evidenceFiles.map((f, i) => (
                    <li key={i}>
                      <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} />
                      <span className="cap-table__evidence-name">{f.name}</span>
                      <Button circular icon size="mini" style={{ backgroundColor: "#648EBC", color: "#fff" }}
                        onClick={() => handleViewFile(f.key)}>
                        <Icon name="eye" />
                      </Button>
                      <Button circular icon size="mini" style={{ backgroundColor: "#A4C4E0", color: "#fff" }}
                        loading={deletingFile === f.key} disabled={deletingFile !== null}
                        onClick={() => handleDeleteFileInline(f.key)}>
                        <Icon name="trash" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          <div className="cap-table__evidence-divider" />

          {/* ── Selector múltiple de empleados ───────────── */}
          <div className="cap-table__emp-multiselect-row">
            <Dropdown
              className="cap-table__emp-multiselect"
              placeholder="Buscar y seleccionar empleados..."
              fluid multiple search selection
              options={employees.filter((e) => !editingData.attendees.some((a) => a.employee === e.value))}
              value={empSelection}
              onChange={(_, { value }) => setEmpSelection(value)}
              noResultsMessage="Sin resultados"
            />
            <Button size="mini" primary disabled={empSelection.length === 0} onClick={addSelectedEmployees}>
              <Icon name="user plus" />
              Agregar{empSelection.length > 0 ? ` (${empSelection.length})` : ""}
            </Button>
          </div>
          <button type="button" className="cap-table__add-manual" onClick={addAttendee}>
            + Agregar asistente sin empleado del sistema
          </button>

          {/* ── Lista de asistentes ──────────────────────── */}
          {editingData.attendees.length > 0 && (
            <div className="cap-table__attendees-count">
              {editingData.attendees.length} asistente{editingData.attendees.length !== 1 ? "s" : ""}
            </div>
          )}
          {editingData.attendees.map((a, idx) => (
            <div key={idx} className="cap-table__attendee-row">
              <input type="text" className="cap-table__inline-input" placeholder="Legajo"
                value={a.legajo} onChange={(e) => setAttendeeField(idx, "legajo", e.target.value)} />
              <input type="text" className="cap-table__inline-input" placeholder="Nombre y apellido"
                value={a.name} onChange={(e) => setAttendeeField(idx, "name", e.target.value)} />
              <InlineSelect
                options={RESULT_OPTIONS}
                value={a.result}
                onChange={(v) => setAttendeeField(idx, "result", v)}
                placeholder="Resultado"
              />
              <Button icon size="mini" color="red" onClick={() => removeAttendee(idx)}>
                <Icon name="trash" />
              </Button>
            </div>
          ))}
        </div>
      ),
    };

    return (
      <div className="cap-table__section-panel">
        <div className="cap-table__section-nav">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button"
              className={`cap-table__section-btn${activeSection === s.key ? " active" : ""}`}
              onClick={() => setActiveSection(activeSection === s.key ? null : s.key)}
            >{s.label}</button>
          ))}
        </div>
        {activeSection && (
          <div className="cap-table__section-body">
            {panelContent[activeSection]}
          </div>
        )}
      </div>
    );
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="cap-table">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Filters */}
      <div className="cap-table__filters">
        <div className="cap-table__search-wrapper">
          <img src={searchSvg} alt="search" className="cap-table__search-icon" />
          <input className="cap-table__search-input" placeholder="Buscar por ID, título, área, tema..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="cap-table__filter-wrapper">
          <Icon name="filter" className="cap-table__filter-icon" />
          <select className="cap-table__filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t("all_statuses")}</option>
            {STATUS_OPTIONS.map((o) => <option key={o.key} value={o.value}>{t(o.value)}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="cap-table__wrapper">
        <Table className="cap-table__tbl" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>ID</Table.HeaderCell>
              <Table.HeaderCell>Título</Table.HeaderCell>
              <Table.HeaderCell>Área</Table.HeaderCell>
              <Table.HeaderCell>Tema</Table.HeaderCell>
              <Table.HeaderCell>Modalidad</Table.HeaderCell>
              <Table.HeaderCell>Fecha</Table.HeaderCell>
              <Table.HeaderCell>Estado</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Acciones</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Seteo</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.length === 0 && editingId !== "new" ? (
              <Table.Row>
                <Table.Cell colSpan={9} textAlign="center" style={{ color: "#999" }}>Sin resultados.</Table.Cell>
              </Table.Row>
            ) : filtered.map((p) =>
              editingId === p._id ? renderEditRow() : (
                <Table.Row key={p._id}
                  onClick={() => { if (!editingId) startEdit(p); }}
                  style={{ cursor: editingId ? "default" : "pointer" }}
                >
                  <Table.Cell style={{ fontWeight: 600, color: "#648EBC" }}>#{p.customId || "-"}</Table.Cell>
                  <Table.Cell>{p.title || "-"}</Table.Cell>
                  <Table.Cell>
                    {p.area?.name ? <Label size="small" color="blue">{p.area.name}</Label> : "-"}
                  </Table.Cell>
                  <Table.Cell>{p.topic?.name || "-"}</Table.Cell>
                  <Table.Cell>{MODALITY_OPTIONS.find((o) => o.value === p.modality)?.text || p.modality || "-"}</Table.Cell>
                  <Table.Cell>{fmtDate(p.date)}</Table.Cell>
                  <Table.Cell>
                    <Label size="small" color={STATUS_COLOR[p.status] || "grey"}>
                      {t(p.status) || p.status || "-"}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center" className="cap-table__actions" onClick={(e) => e.stopPropagation()}>
                    <Popup size="mini" content={t("observations")} trigger={
                      <Button icon size="mini" color="blue" onClick={() => openObs(p)} disabled={!!editingId}>
                        <Icon name="comment outline" />
                      </Button>
                    } />
                    <Popup size="mini" content={t("attach_files")} trigger={
                      <Button icon size="mini" color="olive" onClick={() => openAttach(p)} disabled={!!editingId}>
                        <Icon name="attach" />
                      </Button>
                    } />
                    <Popup size="mini" content={t("view_files")} trigger={
                      <Button icon size="mini" color="violet" onClick={() => openFiles(p)} disabled={!!editingId}>
                        <Icon name="folder open outline" />
                      </Button>
                    } />
                  </Table.Cell>
                  <Table.Cell textAlign="center" className="cap-table__actions" onClick={(e) => e.stopPropagation()}>
                    <Popup size="mini" content={t("edit")} trigger={
                      <Button icon size="mini" color="yellow" onClick={() => startEdit(p)} disabled={!!editingId}>
                        <Icon name="pencil" />
                      </Button>
                    } />
                    <Popup size="mini" content={t("delete")} trigger={
                      <Button icon size="mini" color="red" onClick={() => openDelete(p)} disabled={!!editingId}>
                        <Icon name="trash" />
                      </Button>
                    } />
                  </Table.Cell>
                </Table.Row>
              )
            )}
            {editingId === "new" && renderEditRow()}
          </Table.Body>
        </Table>
      </div>

      {editingId && renderSectionPanel()}

      <ConfirmDialog
        open={showConfirm}
        title={t("delete")}
        message={`¿Eliminar la capacitación #${selected?.customId}?`}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        confirmLabel={t("delete")}
        cancelLabel={t("cancel")}
        confirmDanger={true}
      />

      {/* Observaciones */}
      <BasicModal
        show={showObs}
        close={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); }}
        title={t("observations")}
        size="small"
      >
        <div className="lr-table__modal-inner">
          {obsItem?.observations?.length > 0 && (
            <div className="lr-table__obs-history">
              {[...obsItem.observations].map((obs, origIdx) => ({ obs, origIdx })).reverse().map(({ obs, origIdx }) => {
                const currentUser = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email || "Usuario";
                const isOwner   = obs.userName === currentUser;
                const isEditing = editingObsIdx === origIdx;
                return (
                  <div key={origIdx} className="lr-table__obs-entry">
                    <span className="lr-table__obs-avatar" style={{ background: avatarColor(obs.userName) }}>
                      {getInitials(obs.userName)}
                    </span>
                    <div className="lr-table__obs-body">
                      <div className="lr-table__obs-meta">
                        <span className="lr-table__obs-user">{obs.userName}</span>
                        <span className="lr-table__obs-date">
                          {obs.createdAt ? new Date(obs.createdAt).toLocaleDateString("es-AR") : ""}
                        </span>
                        {isOwner && !isEditing && (
                          <span className="lr-table__obs-actions">
                            <Icon name="pencil" link onClick={() => { setEditingObsIdx(origIdx); setEditingObsVal(obs.text); }} />
                            <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                          </span>
                        )}
                      </div>
                      {isEditing ? (
                        <div>
                          <textarea
                            rows={2}
                            value={editingObsVal}
                            onChange={(e) => setEditingObsVal(e.target.value)}
                            style={{ width: "100%", marginBottom: "0.4rem" }}
                          />
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            <Button size="mini" primary loading={saving} disabled={saving || !editingObsVal.trim()} onClick={handleSaveObsEdit}>Guardar</Button>
                            <Button size="mini" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); }}>Cancelar</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="lr-table__obs-text">{obs.text}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <textarea
            rows={3}
            value={obsVal}
            onChange={(e) => setObsVal(e.target.value)}
            placeholder="Escribir observación..."
            style={{ width: "100%", marginTop: "0.75rem" }}
          />
          <div className="lr-table__modal-actions">
            <Button primary loading={saving} disabled={saving || !obsVal.trim()} onClick={handleSaveObs}>Guardar</Button>
            <Button onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); }}>Cancelar</Button>
          </div>
        </div>
      </BasicModal>

      {/* Adjuntar */}
      <BasicModal show={showAttach} close={() => setShowAttach(false)} title={t("attach_files")} size="small">
        <div>
          <p style={{ color: "#666", marginBottom: "0.5rem" }}>Seleccioná uno o más archivos.</p>
          <input type="file" multiple ref={fileInputRef} style={{ marginBottom: "1rem", display: "block" }} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button primary loading={uploading} disabled={uploading} onClick={handleUpload}>Subir</Button>
            <Button onClick={() => setShowAttach(false)} disabled={uploading}>Cancelar</Button>
          </div>
        </div>
      </BasicModal>

      {/* Ver archivos */}
      <BasicModal show={showFiles} close={() => setShowFiles(false)} title={t("attached_files")} size="small">
        <div>
          {!fileItem?.files?.length ? (
            <p style={{ color: "#999", textAlign: "center" }}>Sin archivos adjuntos.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {fileItem.files.map((f, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
                  <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} />
                  <span style={{ flex: 1, fontSize: "0.88rem" }}>{f.name}</span>
                  <Button circular icon size="mini" style={{ backgroundColor: "#648EBC", color: "#fff" }} onClick={() => handleViewFile(f.key)}>
                    <Icon name="eye" />
                  </Button>
                  <Button circular icon size="mini" style={{ backgroundColor: "#A4C4E0", color: "#fff" }}
                    loading={deletingFile === f.key} disabled={deletingFile !== null}
                    onClick={() => handleDeleteFile(f.key)}>
                    <Icon name="trash" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "0.75rem" }}>
            <Button onClick={() => setShowFiles(false)} style={{ backgroundColor: "#D9D9D9", color: "#555" }}>Cerrar</Button>
          </div>
        </div>
      </BasicModal>

      <BasicModal show={showAreaModal} close={() => setShowAreaModal(false)} title="Nueva área" size="small">
        <AreaForm close={() => setShowAreaModal(false)} onReload={reloadAreas} />
      </BasicModal>

      <BasicModal show={showTopicModal} close={() => setShowTopicModal(false)} title="Nuevo tema" size="small">
        <TopicForm close={() => setShowTopicModal(false)} onReload={reloadTopics} />
      </BasicModal>
    </div>
  );
}
